import { Form } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useEffect, useRef, useState } from 'react';

import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import { ExperienceType } from 'app/typings/experiences';

import { markFormDirty } from '../../hook/useValidationStore';

/**
 * Owns the "unlimited stock" state machine shared by the inventory-visibility toggle and the
 * constant stock config: turning stock unlimited (directly, or by hiding the activity from the
 * inventory) wipes the hotel's manual stock, so both entry points go through one confirmation modal.
 */
export const useStockOverrideState = () => {
  const form = Form.useFormInstance();

  const experienceType = Form.useWatch(['experienceType'], form);
  const isOriginal = experienceType === ExperienceType.STAYCATION;
  const openingConfiguration = Form.useWatch(['openingConfiguration'], form);

  const [isUnlimited, setIsUnlimited] = useState<boolean | null>(null);
  const isUnlimitedInitialized = useRef(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [pendingIsUnlimited, setPendingIsUnlimited] = useState<boolean | null>(
    null
  );
  const [pendingHideInventory, setPendingHideInventory] = useState(false);

  useEffect(() => {
    if (isUnlimitedInitialized.current) {
      return;
    }

    const stockConfig = openingConfiguration?.stockConfiguration;

    if (
      !stockConfig ||
      stockConfig.type !== OPENING_CONFIGURATION_TYPE.CONSTANT
    ) {
      return;
    }

    const stock = stockConfig.configuration?.stock;

    if (stock === null) {
      setIsUnlimited(true);
      isUnlimitedInitialized.current = true;
    } else if (typeof stock === 'number') {
      setIsUnlimited(false);
      isUnlimitedInitialized.current = true;
    }
  }, [openingConfiguration?.stockConfiguration]);

  const isUnlimitedStock = isUnlimited === true || isOriginal;
  const hasExistingStockConfig = isUnlimited !== null || isOriginal;

  const handleUnlimitedCheckboxChange = () => {
    const newIsUnlimited = !isUnlimitedStock;

    if (isUnlimited !== null) {
      setPendingIsUnlimited(newIsUnlimited);
      setShowOverrideModal(true);
    } else {
      markFormDirty();
      setIsUnlimited(newIsUnlimited);
      form.setFieldsValue({
        openingConfiguration: {
          stockConfiguration: {
            configuration: { stock: newIsUnlimited ? null : 0 },
          },
        },
      });
    }
  };

  const handleShowInInventoryChange = (e: RadioChangeEvent) => {
    const newValue = e.target.value;

    if (newValue === false && hasExistingStockConfig) {
      form.setFieldValue('showInInventory', true);
      setPendingHideInventory(true);
      setShowOverrideModal(true);

      return;
    }

    if (newValue === false) {
      setIsUnlimited(true);
      form.setFieldsValue({
        openingConfiguration: {
          stockConfiguration: {
            type: OPENING_CONFIGURATION_TYPE.CONSTANT,
            configuration: { stock: null },
          },
        },
      });
    }

    markFormDirty();
  };

  const handleOverrideConfirm = () => {
    markFormDirty();

    if (pendingHideInventory) {
      setIsUnlimited(true);
      form.setFieldsValue({
        showInInventory: false,
        openingConfiguration: {
          stockConfiguration: {
            type: OPENING_CONFIGURATION_TYPE.CONSTANT,
            configuration: { stock: null },
          },
        },
      });
    } else {
      setIsUnlimited(pendingIsUnlimited);
      form.setFieldsValue({
        openingConfiguration: {
          stockConfiguration: {
            configuration: { stock: pendingIsUnlimited ? null : 0 },
          },
        },
      });
    }

    setShowOverrideModal(false);
    setPendingIsUnlimited(null);
    setPendingHideInventory(false);
  };

  const handleOverrideCancel = () => {
    setShowOverrideModal(false);
    setPendingIsUnlimited(null);
    setPendingHideInventory(false);
  };

  return {
    isUnlimitedStock,
    handleUnlimitedCheckboxChange,
    handleShowInInventoryChange,
    overrideModal: {
      open: showOverrideModal,
      onConfirm: handleOverrideConfirm,
      onCancel: handleOverrideCancel,
    },
  };
};
